const TRANSFORMATION = 'TRANSFORMATION';

function createStepReporter(onStep, log) {
    if (!onStep) return undefined;
    // Rejected here rather than at each caller, because both the constructor and a per-invocation
    // `options.onStep` reach this one place. A non-function would otherwise throw on the first step
    // of a real workflow, where the failure reads as a step fault rather than a wiring mistake.
    if (typeof onStep !== 'function') throw new Error('`onStep` should be a function.');

    return event => {
        let snapshot;
        try {
            snapshot = snapshotEvent(event);
            const returned = onStep(snapshot);
            absorbReturnedPromise(returned, error => reportCallbackFailure(log, error, snapshot.context));
        } catch (error) {
            reportCallbackFailure(log, error, snapshot?.context);
        }
    };
}

function snapshotEvent({ step, context, getContext, error, path, ...event }) {
    return Object.freeze({
        ...event,
        step: snapshotStep(step),
        context: snapshotContext(getContext ? getContext() : context),
        ...(error === undefined ? {} : { error: snapshotError(error) }),
        path: Object.freeze([...path])
    });
}

function snapshotStep(step) {
    return Object.freeze({
        ruleId: stringField(step.ruleId),
        verb: stringField(step.verb),
        namespace: stringField(step.namespace),
        relation: stringField(step.relation),
        status: stringField(step.status),
        description: stringField(step.description),
        isTransformation: step.verb === TRANSFORMATION
    });
}

function snapshotContext(context) {
    return Object.freeze({
        verb: stringField(context?.verb),
        namespace: stringField(context?.namespace),
        relation: stringField(context?.relation),
        status: stringField(context?.status),
        action: stringField(context?.action),
        tenantId: stringField(context?.tenantId),
        correlation: Object.freeze({
            id: stringField(context?.correlation?.id),
            clientId: stringField(context?.correlation?.clientId)
        })
    });
}

function snapshotError(error) {
    return Object.freeze({
        name: stringField(error?.name),
        message: stringField(error?.message),
        stack: stringField(error?.stack),
        aborted: error?.aborted === true
    });
}

function absorbReturnedPromise(returned, onRejected) {
    if (returned === undefined) return;

    try {
        Promise.prototype.then.call(returned, undefined, onRejected);
    } catch {
        Promise.resolve(returned).catch(onRejected);
    }
}

function reportCallbackFailure(log, error, context) {
    try {
        const message = error instanceof Error ? error.message : String(error);
        log.warn(`onStep callback failed: ${message}`, context);
    } catch {
        // A broken logger must not turn an optional reporting failure into a workflow failure.
    }
}

function stringField(value) {
    return typeof value === 'string' ? value : undefined;
}

module.exports = createStepReporter;
